import { getBackendUrl } from '../../lib/api';
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Separator } from '../ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Save, 
  Loader2, 
  Globe, 
  Image as ImageIcon, 
  Upload, 
  Eye,
  AlertCircle,
  Check,
  Sun,
  Moon,
  BarChart3,
  Facebook,
  Code,
  Palette,
  RefreshCw
} from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import SeoColorsSection from './seo/SeoColorsSection';
import SeoTrackingSection from './seo/SeoTrackingSection';
import SeoMetadataSection from './seo/SeoMetadataSection';

const SeoSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ 
    og_image: false, 
    favicon: false,
    logo_light: false,
    logo_dark: false
  });
  const [hasAccess, setHasAccess] = useState(true);
  
  const [seoData, setSeoData] = useState({
    // SEO
    meta_title: '',
    meta_description: '',
    // Images
    og_image_url: '',
    favicon_url: '',
    logo_light_url: '',
    logo_dark_url: '',
    // Tracking
    facebook_pixel_id: '',
    google_analytics_id: '',
    google_tag_manager_id: '',
    // Couleurs
    primary_color: '#0F2744',
    secondary_color: '#4C8F6E',
    button_color: '#4C8F6E',
    button_text_color: '#FFFFFF'
  });

  // Valeurs par défaut pour reset
  const defaultValues = {
    meta_title: 'Talent Partner | Recrutement de talents francophones à Madagascar',
    meta_description: 'Votre partenaire pour recruter des collaborateurs francophones qualifiés à Madagascar. Assistants virtuels, développeurs, experts marketing en remote.',
    primary_color: '#0F2744',
    secondary_color: '#4C8F6E',
    button_color: '#4C8F6E',
    button_text_color: '#FFFFFF'
  };

  // Character counters
  const titleMaxLength = 60;
  const descriptionMaxLength = 155;

  // Helper to get absolute URL for images
  const getAbsoluteUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    const backendUrl = getBackendUrl() || '';
    return `${backendUrl}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  useEffect(() => {
    fetchSeoSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSeoSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/seo-settings');
      
      if (response.status === 403) {
        setHasAccess(false);
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        setSeoData({
          meta_title: data.meta_title || defaultValues.meta_title,
          meta_description: data.meta_description || defaultValues.meta_description,
          og_image_url: data.og_image_url || '',
          favicon_url: data.favicon_url || '',
          logo_light_url: data.logo_light_url || '',
          logo_dark_url: data.logo_dark_url || '',
          facebook_pixel_id: data.facebook_pixel_id || '',
          google_analytics_id: data.google_analytics_id || '',
          google_tag_manager_id: data.google_tag_manager_id || '',
          primary_color: data.primary_color || defaultValues.primary_color,
          secondary_color: data.secondary_color || defaultValues.secondary_color,
          button_color: data.button_color || defaultValues.button_color,
          button_text_color: data.button_text_color || defaultValues.button_text_color
        });
      }
    } catch (error) {
      console.error('Failed to fetch SEO settings:', error);
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await api.put('/admin/seo-settings', seoData);
      
      if (response.ok) {
        toast.success('Paramètres sauvegardés avec succès');
        // Appliquer les couleurs immédiatement
        applyColorsToCSS();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const applyColorsToCSS = () => {
    const root = document.documentElement;
    root.style.setProperty('--tp-primary', seoData.primary_color);
    root.style.setProperty('--tp-secondary', seoData.secondary_color);
    root.style.setProperty('--tp-accent', seoData.secondary_color);
    root.style.setProperty('--tp-button', seoData.button_color);
    root.style.setProperty('--tp-button-text', seoData.button_text_color);
  };

  const handleImageUpload = async (type, file) => {
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml', 'image/x-icon'];
    if (!validTypes.includes(file.type)) {
      toast.error('Format accepté : PNG, JPEG, WebP, SVG, ICO');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Taille maximum : 5MB');
      return;
    }

    setUploading(prev => ({ ...prev, [type]: true }));

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);

      const response = await api.post('/admin/seo-settings/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.ok) {
        const data = await response.json();
        const urlField = {
          'og_image': 'og_image_url',
          'favicon': 'favicon_url',
          'logo_light': 'logo_light_url',
          'logo_dark': 'logo_dark_url'
        }[type];
        
        setSeoData(prev => ({
          ...prev,
          [urlField]: data.url
        }));
        toast.success('Image uploadée avec succès');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'upload');
      }
    } catch (error) {
      console.error('Failed to upload image:', error);
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  const resetToDefaults = (field) => {
    if (defaultValues[field]) {
      setSeoData(prev => ({ ...prev, [field]: defaultValues[field] }));
      toast.info('Valeur par défaut restaurée');
    }
  };

  const resetAllColors = () => {
    setSeoData(prev => ({
      ...prev,
      primary_color: defaultValues.primary_color,
      secondary_color: defaultValues.secondary_color,
      button_color: defaultValues.button_color,
      button_text_color: defaultValues.button_text_color
    }));
    toast.info('Couleurs par défaut restaurées');
  };

  const getCharacterCountColor = (current, max) => {
    const ratio = current / max;
    if (ratio > 1) return 'text-red-500';
    if (ratio > 0.9) return 'text-orange-500';
    return 'text-muted-foreground';
  };

  // Validate tracking IDs format
  const validateFacebookPixel = (id) => !id || /^\d{15,16}$/.test(id);
  const validateGoogleAnalytics = (id) => !id || /^(G-[A-Z0-9]+|UA-\d+-\d+)$/.test(id);
  const validateGTM = (id) => !id || /^GTM-[A-Z0-9]+$/.test(id);

  if (loading) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
        Chargement...
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="py-8 text-center">
        <AlertCircle className="h-12 w-12 text-orange-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-tp-text mb-2">Accès restreint</h3>
        <p className="text-muted-foreground">
          Seuls les Super Administrateurs peuvent accéder à ces paramètres.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs defaultValue="branding" className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="branding" className="flex items-center gap-2">
            <ImageIcon className="h-4 w-4" />
            Logos & Images
          </TabsTrigger>
          <TabsTrigger value="colors" className="flex items-center gap-2">
            <Palette className="h-4 w-4" />
            Couleurs
          </TabsTrigger>
          <TabsTrigger value="seo" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            SEO
          </TabsTrigger>
          <TabsTrigger value="tracking" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Tracking
          </TabsTrigger>
        </TabsList>

        {/* TAB 1: LOGOS & IMAGES */}
        <TabsContent value="branding" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sun className="h-5 w-5 text-yellow-500" />
                Logo sur fond clair
              </CardTitle>
              <CardDescription>
                Logo affiché sur les pages à fond blanc/clair. Format recommandé : PNG ou SVG transparent.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {seoData.logo_light_url && (
                <div className="p-6 border rounded-lg bg-white flex items-center justify-center">
                  <img 
                    src={getAbsoluteUrl(seoData.logo_light_url)} 
                    alt="Logo Light" 
                    className="max-h-20 object-contain"
                  />
                </div>
              )}
              <div>
                <Input
                  id="upload-logo-light"
                  type="file"
                  accept="image/png,image/svg+xml,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload('logo_light', file);
                  }}
                  disabled={uploading.logo_light}
                />
                <Label htmlFor="upload-logo-light">
                  <Button type="button" variant="outline" className="w-full" disabled={uploading.logo_light} asChild>
                    <span className="cursor-pointer flex items-center justify-center">
                      {uploading.logo_light ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Upload en cours...</>
                      ) : seoData.logo_light_url ? (
                        <><Check className="h-4 w-4 mr-2 text-green-500" />Changer le logo</>
                      ) : (
                        <><Upload className="h-4 w-4 mr-2" />Uploader le logo (fond clair)</>
                      )}
                    </span>
                  </Button>
                </Label>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Moon className="h-5 w-5 text-blue-500" />
                Logo sur fond sombre
              </CardTitle>
              <CardDescription>
                Logo affiché sur les pages à fond foncé. Utilisez une version claire/blanche du logo.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {seoData.logo_dark_url && (
                <div className="p-6 border rounded-lg bg-gray-900 flex items-center justify-center">
                  <img 
                    src={getAbsoluteUrl(seoData.logo_dark_url)} 
                    alt="Logo Dark" 
                    className="max-h-20 object-contain"
                  />
                </div>
              )}
              <div>
                <Input
                  id="upload-logo-dark"
                  type="file"
                  accept="image/png,image/svg+xml,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload('logo_dark', file);
                  }}
                  disabled={uploading.logo_dark}
                />
                <Label htmlFor="upload-logo-dark">
                  <Button type="button" variant="outline" className="w-full" disabled={uploading.logo_dark} asChild>
                    <span className="cursor-pointer flex items-center justify-center">
                      {uploading.logo_dark ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Upload en cours...</>
                      ) : seoData.logo_dark_url ? (
                        <><Check className="h-4 w-4 mr-2 text-green-500" />Changer le logo</>
                      ) : (
                        <><Upload className="h-4 w-4 mr-2" />Uploader le logo (fond sombre)</>
                      )}
                    </span>
                  </Button>
                </Label>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Favicon</CardTitle>
              <CardDescription>
                Petite icône affichée dans l'onglet du navigateur. Format : 32x32 ou 64x64 pixels, PNG ou ICO.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {seoData.favicon_url && (
                <div className="flex items-center gap-4 p-4 border rounded-lg bg-muted/30">
                  <img 
                    src={getAbsoluteUrl(seoData.favicon_url)} 
                    alt="Favicon" 
                    className="h-8 w-8 object-contain"
                  />
                  <span className="text-sm text-muted-foreground">Aperçu du favicon</span>
                </div>
              )}
              <div>
                <Input
                  id="upload-favicon"
                  type="file"
                  accept="image/png,image/x-icon"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload('favicon', file);
                  }}
                  disabled={uploading.favicon}
                />
                <Label htmlFor="upload-favicon">
                  <Button type="button" variant="outline" className="w-full" disabled={uploading.favicon} asChild>
                    <span className="cursor-pointer flex items-center justify-center">
                      {uploading.favicon ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Upload en cours...</>
                      ) : seoData.favicon_url ? (
                        <><Check className="h-4 w-4 mr-2 text-green-500" />Changer le favicon</>
                      ) : (
                        <><Upload className="h-4 w-4 mr-2" />Uploader le favicon</>
                      )}
                    </span>
                  </Button>
                </Label>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Image de partage (Open Graph)</CardTitle>
              <CardDescription>
                Image affichée quand vous partagez un lien sur Facebook, LinkedIn, Twitter...
                <br />
                <strong>Format recommandé : 1200 x 630 pixels</strong> (PNG ou JPEG)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {seoData.og_image_url && (
                <div className="border rounded-lg overflow-hidden bg-muted/30">
                  <img 
                    src={getAbsoluteUrl(seoData.og_image_url)} 
                    alt="Open Graph Preview" 
                    className="w-full max-h-[200px] object-cover"
                  />
                </div>
              )}
              <div>
                <Input
                  id="upload-og-image"
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload('og_image', file);
                  }}
                  disabled={uploading.og_image}
                />
                <Label htmlFor="upload-og-image">
                  <Button type="button" variant="outline" className="w-full" disabled={uploading.og_image} asChild>
                    <span className="cursor-pointer flex items-center justify-center">
                      {uploading.og_image ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Upload en cours...</>
                      ) : seoData.og_image_url ? (
                        <><Check className="h-4 w-4 mr-2 text-green-500" />Changer l'image</>
                      ) : (
                        <><Upload className="h-4 w-4 mr-2" />Uploader l'image de partage</>
                      )}
                    </span>
                  </Button>
                </Label>
              </div>

              {/* Social Preview */}
              {(seoData.og_image_url || seoData.meta_title) && (
                <div className="mt-4">
                  <Label className="text-sm text-muted-foreground mb-2 block">Aperçu du partage sur les réseaux sociaux :</Label>
                  <div className="border rounded-lg overflow-hidden max-w-md shadow-sm">
                    {seoData.og_image_url && (
                      <img 
                        src={getAbsoluteUrl(seoData.og_image_url)} 
                        alt="Social Preview" 
                        className="w-full h-[160px] object-cover"
                      />
                    )}
                    <div className="p-3 bg-gray-50">
                      <p className="text-xs text-gray-500 uppercase">
                        {window.location.origin.replace('https://', '').replace('http://', '')}
                      </p>
                      <p className="font-semibold text-gray-900 truncate">
                        {seoData.meta_title || 'Titre de votre page'}
                      </p>
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {seoData.meta_description || 'Description de votre page'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 2: COULEURS */}
        <TabsContent value="colors" className="space-y-6">
          <SeoColorsSection
            seoData={seoData}
            setSeoData={setSeoData}
            resetAllColors={resetAllColors}
          />
        </TabsContent>

        {/* TAB 3: SEO */}
        <TabsContent value="seo" className="space-y-6">
          <SeoMetadataSection
            seoData={seoData}
            setSeoData={setSeoData}
            resetToDefaults={resetToDefaults}
          />
        </TabsContent>

        {/* TAB 4: TRACKING */}
        <TabsContent value="tracking" className="space-y-6">
          <SeoTrackingSection seoData={seoData} setSeoData={setSeoData} />
        </TabsContent>
      </Tabs>

      {/* Save Button - Always visible */}
      <div className="flex justify-end pt-4 border-t">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="min-w-[200px]"
          style={{ 
            backgroundColor: seoData.button_color,
            color: seoData.button_text_color
          }}
          data-testid="save-seo-button"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Sauvegarde en cours...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Sauvegarder tous les paramètres
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default SeoSettings;
